import { isLocalhost } from "@/Configs/config";
import { setMultiTenantHeaders } from "@/Utils/headerUtils";

import moment from "moment-timezone";
import { Site as SelectedSite } from "server/sites/index.js";

import { timezoneStore } from "./hooks/useConfig";

export type ExportType = "csv" | "excel" | "zip";

export const fileExtensionForType = (type: ExportType) => {
  switch (type) {
    case "csv":
      return "csv";
    case "excel":
      return "xlsx";
    case "zip":
      return "zip";
    default:
      throw new Error("Invalid type");
  }
};

const ENTITY_FILE_NAME = {
  customers: "exportLabels:customersFileName",
  events: "exportLabels:eventsFileName",
};

export const downloadExportFile = async ({
  type,
  entity,
  queryParams,
  terminalId,
  getTokenSilently,
  language,
  t,
  showPeriodInTitle,
  selectedEventIds,
  selectedSite,
}: {
  type: ExportType;
  entity: "customers" | "events";
  queryParams: { [key: string]: any };
  terminalId?: string;
  getTokenSilently: () => Promise<string | undefined>;
  language: string;
  t: (key: string) => string;
  showPeriodInTitle?: boolean;
  selectedEventIds?: string[];
  selectedSite: SelectedSite | undefined;
}) => {
  const { timezone } = timezoneStore.getState();
  const token = await getTokenSilently();

  const headers: HeadersInit = {
    language,
    authorization: token as string,
    "Content-Type": "application/json",
  };
  setMultiTenantHeaders(selectedSite, headers);

  const exportUrl = getExportUrl(`${entity}/${type}`);
  const response = await fetch(exportUrl, {
    method: "POST",
    headers,
    body: JSON.stringify({ ...queryParams, terminalId, selectedEventIds }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);

  let filename = "";
  const entityFileName = ENTITY_FILE_NAME[entity];
  if (showPeriodInTitle) {
    const { startTime, endTime } = queryParams;

    const startDate = moment(startTime).tz(timezone).format("YYYYMMDD");

    const endDateTimestamp = endTime || moment().tz(timezone).toISOString();
    const endDate = moment(endDateTimestamp).tz(timezone).format("YYYYMMDD");
    filename = `${t(entityFileName)}_${startDate}-${endDate}.${fileExtensionForType(type)}`;
  } else {
    filename = `${t(entityFileName)}_${moment().tz(timezone).format("YYYYMMDD")}.${fileExtensionForType(type)}`;
  }

  addAndTriggerDownloadElement(url, filename);
};

export const getExportUrl = (path: string) => {
  const serverPort = import.meta.env.VITE_SERVER_PORT ?? "4000";
  const host = isLocalhost ? `http://localhost:${serverPort}` : "";
  return `${host}/api/files/${path}`;
};

export const addAndTriggerDownloadElement = (url: string, filename: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
